import React, { FunctionComponent } from 'react';
import { FormControl, InputAdornment, OutlinedInput } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Button from './Button';
import clsx from 'clsx';

export interface Props {
    value: string;
    name?: string;
    label: string;
    startIcon?: IconProp;
    blackButton?: boolean;
    classNameCustom?: string;
    disabled?: boolean;
    readOnly?: boolean;
    onChange?: (value: string) => void;
    onClick?: (event: any) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            minWidth: 0, // Allow container to shrink below content size
            flex: '1 1 auto' // Allow flex item to shrink
        },
        button: {
            'height': 39,
            'padding': '0px 28px',
            '& .MuiButton-label': {
                fontSize: 14,
                fontWeight: 600
            }
        },
        blackButton: {
            'color': '#FFFFFF',
            'backgroundColor': '#222222',
            '&:hover': {
                color: '#FFFFFF',
                backgroundColor: '#222222'
            }
        },
        input: {
            'fontSize': 16,
            'fontWeight': 400,
            'borderRadius': 30,
            'paddingRight': 8,
            'width': '100%',
            'minWidth': 0, // Allow input to shrink below content size

            [theme.breakpoints.up('md')]: {
                fontSize: 18
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px !important',
                borderColor: '#222222 !important'
            },
            "&:not(.Mui-error) input:not([value='']) ~ .MuiOutlinedInput-notchedOutline": {
                borderWidth: '1px !imporant',
                borderColor: '#222222 !important'
            },
            "&.onlineBookingInput input:not([value='']) ~ .MuiOutlinedInput-notchedOutline": {
                borderWidth: '1px !imporant',
                borderColor: '#D3D3D3 !important'
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: '#EA6464 !important'
            },
            '& .MuiOutlinedInput-input': {
                'overflow': 'hidden !important',
                'text-overflow': 'ellipsis !important',
                'white-space': 'nowrap !important'
            },
            '& input': {
                'height': 54,
                'padding': '0px 0px 0px 20px',
                'overflow': 'hidden !important',
                'text-overflow': 'ellipsis !important',
                'white-space': 'nowrap !important',
                'width': '100%',
                'maxWidth': '100%',
                'caret-color': 'transparent', // Hide cursor when readOnly

                '&:focus': {
                    'overflow': 'hidden !important',
                    'text-overflow': 'ellipsis !important',
                    'white-space': 'nowrap !important'
                },

                '&::placeholder': {
                    opacity: 1,
                    color: '#7A7A7A'
                }
            },
            '& input[readonly]': {
                'cursor': 'default',
                'user-select': 'none',
                '-webkit-user-select': 'none',
                '-moz-user-select': 'none',
                '-ms-user-select': 'none'
            },
            '&.Mui-disabled': {
                background: '#fafafa'
            }
        }
    })
);

export const TextFieldWithButton: FunctionComponent<Props> = ({
    value,
    name,
    label,
    startIcon,
    blackButton,
    classNameCustom,
    disabled,
    readOnly = false,
    onChange,
    onClick
}) => {
    const classes = useStyles();

    const handleChange = (event: any): void => {
        onChange && onChange(event.target.value);
    };

    const handleFocus = (event: any): void => {
        // Prevent text selection and maintain ellipsis when readOnly
        if (readOnly) {
            event.target.setSelectionRange(0, 0);
            // Keep focus but prevent text selection
            setTimeout(() => {
                event.target.setSelectionRange(0, 0);
            }, 0);
        }
    };

    const handleMouseDown = (event: any): void => {
        // Prevent text selection when readOnly
        if (readOnly) {
            event.preventDefault();
            // Prevent default selection behavior
            const input = event.target;
            if (input && input.setSelectionRange) {
                input.setSelectionRange(0, 0);
            }
        }
    };

    const handleSelect = (event: any): void => {
        // Prevent text selection when readOnly
        if (readOnly) {
            event.preventDefault();
            event.target.setSelectionRange(0, 0);
        }
    };

    return (
        <FormControl
            variant="outlined"
            className={classes.container}
        >
            <OutlinedInput
                name={name}
                className={clsx(classes.input, classNameCustom)}
                id="outlined-adornment-password"
                type="text"
                placeholder="Bath, Nail Trimming, Haircut"
                value={value}
                onChange={handleChange}
                onFocus={handleFocus}
                onMouseDown={handleMouseDown}
                onSelect={handleSelect}
                disabled={disabled}
                readOnly={readOnly}
                endAdornment={
                    <InputAdornment position="end">
                        <Button
                            color="black"
                            disabled={disabled}
                            label={label}
                            id="copy_button"
                            startIcon={startIcon}
                            className={clsx(classes.button, {
                                [classes.blackButton]: blackButton
                            })}
                            onClick={onClick}
                        />
                    </InputAdornment>
                }
            />
        </FormControl>
    );
};

export default TextFieldWithButton;
